import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { appointmentAPI, technicianAPI } from '../../services/api';
import { COLORS } from "../../constants/colors"
// Loading skeleton component
const StatsSkeleton = () => (
  <Paper sx={{ p: 3, mt: 3 }}>
    <Skeleton variant="text" sx={{ fontSize: '2rem', width: '40%' }} />
    <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '30%', mb: 3 }} />
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {[...Array(4)].map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        </Grid>
      ))}
    </Grid>
    <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 3 }} />
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
  </Paper>
);

// Chart wrapper component to fix positioning issues
const ChartWrapper = ({ title, children, height = 400 }) => (
  <Card sx={{ mb: 3, overflow: 'visible' }}>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <div style={{ width: '100%', height: height, position: 'relative' }}>
        {children}
      </div>
    </CardContent>
  </Card>
);

function groupByMonth(appointments) {
  const result = {};
  appointments.forEach(app => {
    const date = new Date(app.scheduledAt);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!result[key]) {
      result[key] = {
        count: 0,
        totalIncome: 0,
        totalProfit: 0
      };
    }
    result[key].count++;
    result[key].totalIncome += app.price || 0;
    result[key].totalProfit += app.profit || 0;
  });
  return result;
}

function groupByStatus(appointments) {
  const result = {};
  appointments.forEach(app => {
    const status = app.status || 'unknown';
    if (!result[status]) result[status] = 0;
    result[status]++;
  });
  return result;
}

function groupByBranch(appointments) {
  const result = {};
  appointments.forEach(app => {
    const branchName = app.branch?.name || 'Unknown';
    if (!result[branchName]) result[branchName] = 0;
    result[branchName]++;
  });
  return result;
}

function groupByScan(appointments) {
  const result = {};
  appointments.forEach(app => {
    if (app.scans && Array.isArray(app.scans)) {
      app.scans.forEach(scanItem => {
        const scanName = scanItem.scan?.name || 'Unknown';
        if (!result[scanName]) result[scanName] = 0;
        result[scanName] += scanItem.quantity || 1;
      });
    }
  });
  return result;
}

const RadiologyTechnicianStats = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [technician, setTechnician] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState({});
  const [statusStats, setStatusStats] = useState({});
  const [branchStats, setBranchStats] = useState({});
  const [scanStats, setScanStats] = useState({});
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [techResponse, appResponse] = await Promise.all([
          technicianAPI.getById(id),
          appointmentAPI.getAllAppointments({ technicianId: id })
        ]);

        // Extract nested data correctly
        const techData = techResponse.data?.data || techResponse.data;
        const appointmentsData = appResponse.data?.data || appResponse.data || [];
        const totalsData = appResponse.data?.totals || null;

        // Ensure appointments is an array
        const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];

        const groupedStats = groupByMonth(appointments);
        const statusGrouped = groupByStatus(appointments);
        const branchGrouped = groupByBranch(appointments);
        const scanGrouped = groupByScan(appointments);

        setMonthlyStats(groupedStats);
        setStatusStats(statusGrouped);
        setBranchStats(branchGrouped);
        setScanStats(scanGrouped);
        setTotals(totalsData);
        setTechnician(techData);
      } catch (err) {
        console.error('Error fetching technician data:', err);
        setError(err.message || t('technicianStats.errorFetching') || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, t]);

  // Transform data for charts
  const monthlyChartData = Object.entries(monthlyStats)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, data]) => ({
      month,
      appointments: data.count,
      income: data.totalIncome,
      profit: data.totalProfit
    }));

  const statusChartData = Object.entries(statusStats).map(([status, count]) => ({
    name: t(`technicianStats.status.${status}`) || status,
    value: count
  }));

  const branchChartData = Object.entries(branchStats).map(([branch, count]) => ({
    name: branch,
    appointments: count
  }));

  const scanChartData = Object.entries(scanStats)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([scan, count]) => ({
      name: scan,
      count
    }));

  // Show loading skeleton
  if (loading) {
    return <StatsSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h4" component="h1">
                {t('technicianStats.title') || 'Technician Statistics'}
              </Typography>
              {totals && (
                <Chip
                  label={`${totals.totalAppointments || 0} ${t('technicianStats.appointments') || 'appointments'}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Technician Info */}
          {technician && (
            <Box textAlign={'right'}>
              <Typography variant="h5" color="primary" gutterBottom textAlign={'right'} dir="rtl">
                {technician.name}
                {technician.age && (
                  <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                    ({t('technicianStats.age') || 'Age'}: {technician.age})
                  </Typography>
                )}
              </Typography>
              {technician.phone && (
                <Typography variant="body2" color="text.secondary">
                  {t('technicianStats.phone') || 'Phone'}: {technician.phone}
                </Typography>
              )}
              {technician.email && (
                <Typography variant="body2" color="text.secondary">
                  {t('technicianStats.email') || 'Email'}: {technician.email}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.main', color: 'white', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('technicianStats.totalAppointments') || 'Total Appointments'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {totals.totalAppointments || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.main', color: 'white', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('technicianStats.totalIncome') || 'Total Income'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {(totals.totalIncome || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.main', color: 'white', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('technicianStats.totalScans') || 'Total Scans'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {totals.totalScans || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.main', color: 'white', height: '100%' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('technicianStats.totalPatients') || 'Total Patients'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {totals.totalPatients || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Monthly Appointments Bar Chart */}
      <ChartWrapper
        title={t('technicianStats.monthlyAppointments') || 'Monthly Appointments'}
        height={400}
      >
        {monthlyChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(value, name) => [
                  value,
                  name === 'appointments'
                    ? t('technicianStats.appointments') || 'Appointments'
                    : name
                ]}
              />
              <Legend
                formatter={(value) =>
                  value === 'appointments'
                    ? t('technicianStats.appointments') || 'Appointments'
                    : value
                }
              />
              <Bar
                dataKey="appointments"
                fill="#8884d8"
                name="appointments"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Alert severity="info">
            {t('technicianStats.noDataAvailable') || 'No data available'}
          </Alert>
        )}
      </ChartWrapper>

      {/* Income & Profit Line Chart */}
      <ChartWrapper
        title={t('technicianStats.monthlyIncomeProfit') || 'Monthly Income & Profit'}
        height={400}
      >
        {monthlyChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(value, name) => [
                  value.toLocaleString(),
                  name === 'income'
                    ? t('technicianStats.income') || 'Income'
                    : t('technicianStats.profit') || 'Profit'
                ]}
              />
              <Legend
                formatter={(value) =>
                  value === 'income'
                    ? t('technicianStats.income') || 'Income'
                    : t('technicianStats.profit') || 'Profit'
                }
              />
              <Line
                type="monotone"
                dataKey="income"
                stroke="#82ca9d"
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="#8884d8"
                strokeWidth={3}
                dot={{ r: 5 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Alert severity="info">
            {t('technicianStats.noDataAvailable') || 'No data available'}
          </Alert>
        )}
      </ChartWrapper>

      {/* Status & Branch Charts Side by Side */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Status Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('technicianStats.appointmentsByStatus') || 'Appointments by Status'}
              </Typography>
              <div style={{ width: '100%', height: 350, position: 'relative' }}>
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    {t('technicianStats.noDataAvailable') || 'No data available'}
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Branch Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('technicianStats.appointmentsByBranch') || 'Appointments by Branch'}
              </Typography>
              <div style={{ width: '100%', height: 350, position: 'relative' }}>
                {branchChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={branchChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip
                        formatter={(value) => [
                          value,
                          t('technicianStats.appointments') || 'Appointments'
                        ]}
                      />
                      <Bar
                        dataKey="appointments"
                        fill="#00C49F"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    {t('technicianStats.noDataAvailable') || 'No data available'}
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Scans Chart */}
      <ChartWrapper
        title={t('technicianStats.topScans') || 'Top Scans Performed'}
        height={400}
      >
        {scanChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scanChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip
                formatter={(value) => [
                  value,
                  t('technicianStats.count') || 'Count'
                ]}
              />
              <Legend
                formatter={() => t('technicianStats.scanCount') || 'Scan Count'}
              />
              <Bar
                dataKey="count"
                fill="#FF8042"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Alert severity="info">
            {t('technicianStats.noDataAvailable') || 'No data available'}
          </Alert>
        )}
      </ChartWrapper>

      {/* Monthly Stats Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('technicianStats.monthlyStatsTable') || 'Monthly Statistics Details'}
          </Typography>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {t('technicianStats.month') || 'Month'}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {t('technicianStats.totalAppointments') || 'Total Appointments'}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {t('technicianStats.totalIncome') || 'Total Income'}
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  {t('technicianStats.totalProfit') || 'Total Profit'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(monthlyStats).length > 0 ? (
                Object.entries(monthlyStats)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([month, data]) => (
                    <TableRow key={month} hover>
                      <TableCell>{month}</TableCell>
                      <TableCell>{data.count}</TableCell>
                      <TableCell>{data.totalIncome.toLocaleString()}</TableCell>
                      <TableCell>{data.totalProfit.toLocaleString()}</TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      {t('technicianStats.noDataAvailable') || 'No data available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RadiologyTechnicianStats;